import { type CSSProperties, type MouseEvent, memo, type ReactNode } from 'react';

import { cn } from '@air/utils-tailwind';

import blueStyles from '~/components/v2/components/workspace/onboarding-intro/walkthrough/onboardingIntroWalkthrough.module.css';

type AnimatedOnboardingUploadActionPalette = 'smart-resize' | 'translate';

type AnimatedOnboardingUploadShimmerStyle = CSSProperties & {
  '--shimmer-pill-delay': string;
  '--shimmer-pill-duration': string;
};

export type AnimatedOnboardingUploadActionProps = {
  icon: ReactNode;
  label: string;
  onClick: (event: MouseEvent<HTMLButtonElement>) => void;
  palette: AnimatedOnboardingUploadActionPalette;
  slotIndex: number;
  testId: string;
};

/** One pill glimmers at a time, so the cycle is one slot each and every pill offsets by its own slot. */
const ACTION_SHIMMER_SLOT_SECONDS = 4;

const ACTION_SHIMMER_SLOT_COUNT = 2;

const getActionShimmerStyle = (slotIndex: number): AnimatedOnboardingUploadShimmerStyle => ({
  '--shimmer-pill-delay': `-${slotIndex * ACTION_SHIMMER_SLOT_SECONDS}s`,
  '--shimmer-pill-duration': `${ACTION_SHIMMER_SLOT_COUNT * ACTION_SHIMMER_SLOT_SECONDS}s`,
});

export const AnimatedOnboardingUploadAction = memo(
  ({ icon, label, onClick, palette, slotIndex, testId }: AnimatedOnboardingUploadActionProps) => (
    <div
      className={cn(
        'canvas-composer-border-shimmer canvas-composer-border-shimmer--chromatic canvas-composer-border-shimmer--pill rounded-full p-px',
        palette === 'translate' && 'canvas-composer-border-shimmer--translate',
        palette === 'smart-resize' && 'canvas-composer-border-shimmer--smart-resize',
      )}
      style={getActionShimmerStyle(slotIndex)}
    >
      <div aria-hidden className={cn('canvas-composer-border-shimmer-clip', blueStyles.outlineSparkleRing)}>
        <div className="canvas-composer-border-spinner" />
      </div>
      <button
        className="relative z-1 flex items-center gap-2 rounded-full border border-white/70 ads:bg-raised/45 bg-white/35 px-5 py-3 font-medium ads:text-primary text-14 text-gray-12 shadow-[0_18px_44px_rgba(16,149,237,0.22)] backdrop-blur-[40px] transition-colors ads:hover:bg-raised/65 hover:bg-white/55 focus-visible:outline-none ads:focus-visible:ring-focus focus-visible:ring-2 focus-visible:ring-altitude-8 dark:border-white/10 dark:bg-gray-2/45"
        data-testid={testId}
        data-theme="light"
        onClick={onClick}
        type="button"
      >
        {icon}
        {label}
      </button>
    </div>
  ),
);

AnimatedOnboardingUploadAction.displayName = 'AnimatedOnboardingUploadAction';
